import { useCallback, useRef, useState } from 'react';
import type {
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import type { BoardColumnCoreFragment } from '../../../generated/graphql';
import type { CardMoveArgs } from './useBoardDragHandlers';
import { cardIdOf, columnIdOf } from './drag-ids';
import { columnOfCard } from './board-order';
import { dragTrace } from './drag-trace';

type Column = BoardColumnCoreFragment;

interface UseBoardCardSlottingArgs {
  columns: Column[];
  onCardMove: (args: CardMoveArgs) => void;
  // Existing over-based handler; used for within-column card moves and all
  // column moves. Cross-column card moves are committed from the slotting
  // arrangement instead (see onDragEnd).
  fallbackDragEnd: (event: DragEndEvent) => void;
}

/** Same cards in the same order? Compared by id, ignoring object identity. */
const sameCardOrder = (
  a: readonly { _id: string }[],
  b: readonly { _id: string }[]
) => a.length === b.length && a.every((card, i) => card._id === b[i]._id);

// dnd-kit renders each column as its own vertical SortableContext, so a card
// dragged over a DIFFERENT column leaves the sibling cards unmoved — the
// target context doesn't reserve space for an item it doesn't own. This hook
// keeps a transient clone of the layout while a card is in flight and, on
// `onDragOver`, splices the active card into the hovered column. That makes
// the target context include it and open a real gap at the drop slot. The
// clone is null whenever no card drag is active, so normal renders use the
// live (server/optimistic) data untouched.
export function useBoardCardSlotting({
  columns,
  onCardMove,
  fallbackDragEnd,
}: UseBoardCardSlottingArgs) {
  const [slotting, setSlotting] = useState<Column[] | null>(null);
  const sourceColumnId = useRef<string | null>(null);

  const onDragStart = useCallback(
    (event: DragStartEvent) => {
      if (event.active.data.current?.type !== 'card') return;
      const id = cardIdOf(event.active.id);
      sourceColumnId.current = columnOfCard(columns, id)?._id ?? null;
      dragTrace('start', { card: id, from: sourceColumnId.current });
      // Shallow-clone columns and their card arrays; card objects can be
      // shared since we only move them between lists, never mutate them.
      setSlotting(columns.map((c) => ({ ...c, cards: c.cards.slice() })));
    },
    [columns]
  );

  const onDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.data.current?.type !== 'card') return;
    const id = cardIdOf(active.id);
    const overIsColumn = over.data.current?.type === 'column';
    const overColumnId = overIsColumn
      ? columnIdOf(over.id)
      : (over.data.current?.columnId as string | undefined);
    if (!overColumnId) return;

    setSlotting((prev) => {
      if (!prev) return prev;
      const from = columnOfCard(prev, id);
      const to = prev.find((c) => c._id === overColumnId);
      if (!from || !to) return prev;
      // The rule: dnd-kit's own SortableContext renders the gap for the
      // column the drag STARTED in; this hook renders it for every other
      // column. Note this is deliberately keyed off the drag's origin, not
      // off `from === to` — once the card has been slotted into a foreign
      // column, `from` IS that foreign column, so a `from === to` check
      // would read as "same column" and freeze the card at whichever slot
      // it first crossed into.
      const isSourceColumn = to._id === sourceColumnId.current;
      if (from._id === to._id && isSourceColumn) {
        return prev;
      }
      const card = from.cards.find((k) => k._id === id);
      if (!card) return prev;

      // Hovering the card's own slot means nothing has changed.
      const overCardId = overIsColumn ? null : cardIdOf(over.id);
      if (overCardId === id) return prev;

      // Index math must ignore the moving card, otherwise its own presence
      // in the target list shifts every index by one.
      const without = to.cards.filter((k) => k._id !== id);
      // Tail when hovering the column body / an empty column.
      let insertAt = without.length;
      if (overCardId) {
        const idx = without.findIndex((k) => k._id === overCardId);
        if (idx >= 0) {
          // Which SIDE of the hovered card the pointer is on decides the
          // slot: top half inserts before it, bottom half after it. Without
          // this the insert is always "before", so releasing below card 2
          // makes the dragged card the new card 2 rather than the new 3 —
          // there is no way to express "put it last".
          const activeRect = active.rect.current.translated;
          const overRect = over.rect;
          const isBelowMidpoint =
            !!activeRect &&
            activeRect.top + activeRect.height / 2 >
              overRect.top + overRect.height / 2;
          insertAt = isBelowMidpoint ? idx + 1 : idx;
        }
      }

      const toCards = without.slice();
      toCards.splice(insertAt, 0, card);
      const sameColumn = from._id === to._id;
      const fromCards = sameColumn
        ? toCards
        : from.cards.filter((k) => k._id !== id);

      // onDragOver fires on every pointer move, but the slot only changes on
      // a few of them. Returning `prev` unchanged on the rest preserves the
      // column object identities that React.memo on SortableBoardColumn /
      // BoardColumn / SortableBoardCard depends on — otherwise every tick
      // deep-re-rendered both touched columns (re-running useSortable on
      // each card) for the entire duration of the drag.
      const unchanged =
        sameCardOrder(to.cards, toCards) &&
        (sameColumn || sameCardOrder(from.cards, fromCards));

      dragTrace('over', {
        card: id,
        over: overCardId ?? '(column body)',
        overType: overIsColumn ? 'column' : 'card',
        to: to._id,
        from: from._id,
        slot: insertAt,
        of: toCards.length,
        unchanged,
      });

      if (unchanged) return prev;

      return prev.map((c) => {
        // `to` is checked FIRST so a same-column re-splice (from === to)
        // reinserts the card instead of just dropping it.
        if (c._id === to._id) return { ...c, cards: toCards };
        if (c._id === from._id) return { ...c, cards: fromCards };
        return c;
      });
    });
  }, []);

  const finish = useCallback(() => {
    setSlotting(null);
    sourceColumnId.current = null;
  }, []);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active } = event;
      const layout = slotting;
      if (active.data.current?.type === 'card' && layout) {
        const id = cardIdOf(active.id);
        const target = columnOfCard(layout, id);
        // Cross-column: commit from the slotting arrangement. This is immune
        // to dnd-kit resolving `over` to the active card's own slot (which
        // would no-op the over-based handler and snap the card back).
        if (target && target._id !== sourceColumnId.current) {
          const idx = target.cards.findIndex((k) => k._id === id);
          const afterCardId = idx > 0 ? target.cards[idx - 1]._id : undefined;
          dragTrace('end', {
            path: 'slotting',
            card: id,
            to: target._id,
            after: afterCardId,
            atIndex: idx,
          });
          onCardMove({ cardId: id, toColumnId: target._id, afterCardId });
          finish();
          return;
        }
      }
      // Within-column card move, or any column move → existing handler.
      dragTrace('end', {
        path: 'fallback',
        active: event.active.id,
        over: event.over?.id,
        overType: event.over?.data.current?.type,
      });
      fallbackDragEnd(event);
      finish();
    },
    [slotting, onCardMove, fallbackDragEnd, finish]
  );

  const onDragCancel = useCallback(() => finish(), [finish]);

  return {
    displayColumns: slotting ?? columns,
    onDragStart,
    onDragOver,
    onDragEnd,
    onDragCancel,
  };
}
